import { useState, useEffect } from "react";
import axios from "axios";
import { ClipboardList, CheckCircle, Clock, XCircle, Users, Calendar, TrendingUp } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [recentRegs, setRecentRegs] = useState([]);

  useEffect(() => {
    axios.get(`${API}/admin/stats`, { withCredentials: true }).then(r => setStats(r.data)).catch(() => {});
    axios.get(`${API}/registrations`, { withCredentials: true }).then(r => setRecentRegs(r.data.slice(0, 5))).catch(() => {});
  }, []);

  const statCards = stats ? [
    { label: "Total Inscripciones", value: stats.total, icon: ClipboardList, color: "#0072A4", bg: "#EFF6FF" },
    { label: "Pendientes", value: stats.pending, icon: Clock, color: "#F59E0B", bg: "#FFFBEB" },
    { label: "Confirmadas", value: stats.confirmed, icon: CheckCircle, color: "#3CB44B", bg: "#F0FDF4" },
    { label: "Pagadas", value: stats.paid, icon: TrendingUp, color: "#059669", bg: "#ECFDF5" },
    { label: "Semana 1", value: stats.semana_1, icon: Calendar, color: "#8B5CF6", bg: "#F5F3FF" },
    { label: "Semana 2", value: stats.semana_2, icon: Calendar, color: "#F58220", bg: "#FFF7ED" },
  ] : [];

  const STATUS_LABELS = { pending: "Pendiente", confirmed: "Confirmada", cancelled: "Cancelada" };
  const STATUS_COLORS = { pending: "bg-yellow-100 text-yellow-800", confirmed: "bg-green-100 text-green-800", cancelled: "bg-red-100 text-red-800" };

  return (
    <div data-testid="admin-dashboard">
      <div className="mb-8">
        <h1 className="font-['Montserrat'] font-bold text-2xl text-[#0F172A] mb-1">Dashboard</h1>
        <p className="text-[#475569] font-['Montserrat'] text-sm">Resumen del Campus Verano Activo 2026</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
        {statCards.map((card) => (
          <div
            key={card.label}
            data-testid={`stat-${card.label.replace(/\s+/g, '-').toLowerCase()}`}
            className="bg-white rounded-xl p-4 border border-[#E2E8F0] hover:shadow-md transition-shadow"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: card.bg }}>
                <card.icon size={16} color={card.color} />
              </div>
            </div>
            <div className="font-['Montserrat'] font-bold text-2xl text-[#0F172A]">{card.value ?? "—"}</div>
            <div className="font-['Montserrat'] text-xs text-[#475569] mt-1">{card.label}</div>
          </div>
        ))}
      </div>

      {/* Recent registrations */}
      <div className="bg-white rounded-xl border border-[#E2E8F0] overflow-hidden">
        <div className="px-6 py-4 border-b border-[#E2E8F0] flex items-center justify-between">
          <div className="flex items-center gap-2">
            <TrendingUp size={18} color="#0072A4" />
            <h2 className="font-['Montserrat'] font-semibold text-[#0F172A] text-base">Últimas Inscripciones</h2>
          </div>
          <a href="/admin/inscripciones" className="font-['Montserrat'] text-xs text-[#0072A4] hover:underline">Ver todas →</a>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-[#F8FAFC]">
                <th className="px-6 py-3 text-left font-['Montserrat'] text-xs font-semibold text-[#475569] uppercase tracking-wide">Participante</th>
                <th className="px-6 py-3 text-left font-['Montserrat'] text-xs font-semibold text-[#475569] uppercase tracking-wide">Tutor</th>
                <th className="px-6 py-3 text-left font-['Montserrat'] text-xs font-semibold text-[#475569] uppercase tracking-wide">Semana</th>
                <th className="px-6 py-3 text-left font-['Montserrat'] text-xs font-semibold text-[#475569] uppercase tracking-wide">Estado</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#F1F5F9]">
              {recentRegs.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-6 py-8 text-center text-[#94A3B8] font-['Montserrat'] text-sm">
                    No hay inscripciones todavía
                  </td>
                </tr>
              ) : recentRegs.map((reg) => (
                <tr key={reg._id} className="hover:bg-[#F8FAFC]">
                  <td className="px-6 py-3 font-['Montserrat'] text-sm text-[#0F172A]">{reg.nombre_nino}</td>
                  <td className="px-6 py-3 font-['Montserrat'] text-sm text-[#475569]">{reg.nombre_tutor}</td>
                  <td className="px-6 py-3 font-['Montserrat'] text-xs text-[#475569]">
                    {reg.semana_1 && "S1 "}{reg.semana_2 && "S2"}
                  </td>
                  <td className="px-6 py-3">
                    <span className={`font-['Montserrat'] text-xs font-semibold px-2 py-1 rounded-full ${STATUS_COLORS[reg.status] || "bg-gray-100 text-gray-700"}`}>
                      {STATUS_LABELS[reg.status] || reg.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
